import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to Make a Piechart using React and d3",
  "description": "",
  "published": "2016-04-15T00:00:00.000Z",
  "redirect_from": ["/blog/how-to-make-a-piechart-using-react-and-d3/swizec/6785"],
  "categories": "d3js, Front End, react, react+d3js, reactjs, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Piecharts might be the single most mocked chart in the world, but they’re great. Is there a better way to show proportions?`}</p>
    <p>{`Of course, it all breaks down when you have more than a couple data points. And yes, it’s impossible to gauge the difference between 24% and 26% on a piechart. Not to mention the accessibility issues when the charts rely on colors to distinguish slices.`}</p>
    <p>{`If you google "never use a piechart”, 597,000 results show up. That’s a bunch of hate for the poor piechart.`}</p>
    <p><strong parentName="p">{`But`}</strong>{` they’re easy and fun to make. They’re perfect for showing overwhelming proportions, and there’s nothing like a piechart to make an enterprise presentation more enterprisey.`}</p>
    <p>{`Especially if you make it 3D.`}</p>
    <p>{`When I say that a piechart is easy to make, I mean that with 86 lines of very beautiful and readable code, you can make one that looks great. 86 lines might sound like a lot for a piechart, but 33 of those are empty lines or closing braces. That’s 38%.`}</p>
    <p>{`Let me show you with a piechart :)`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/04/Screen-Shot-2016-04-12-at-02.08.11-300x122.png",
        "alt": "Screen Shot 2016-04-12 at 02.08.11"
      }}></img></p>
    <p>{`See, piecharts `}<em parentName="p">{`work`}</em>{`. To build this, we’re going to:`}</p>
    <ul>
      <li parentName="ul">{`use a d3 layout to calculate a piechart`}</li>
      <li parentName="ul">{`use React stuff to render the piechart`}</li>
      <li parentName="ul">{`build a generic Piechart component`}</li>
    </ul>
    <p>{`At the end of this tutorial, you’ll have piechart component that you can use like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<piechart x={100} y={100} outerradius={100} innerradius={50} data="{[{value:" 92-34,="" label:="" 'code="" lines'},="" {value:="" 34,="" 'empty="" lines'}]}="">

</piechart>
`}</code></pre>
    <p>{`Pretty great, huh?`}</p>
    <p>{`We’re going to build 3 components:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`Piechart`}</inlineCode>{` - the piechart itself`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`Arc`}</inlineCode>{` - each of the slices`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`LabeledArc`}</inlineCode>{` - a slice with some text`}</li>
    </ol>
    <h2 {...{
      "id": "piechart"
    }}>{`Piechart`}</h2>
    <p>{`This is the piechart’s main component. It takes data and draws the arcs. That’s all it does.`}</p>
    <p>{`The component skeleton for `}<inlineCode parentName="p">{`Piechart`}</inlineCode>{` looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx

import React, { Component } from "react";

import { LabeledArc } from "./Arc";

class Piechart extends Component {
  constructor() {}

  arcGenerator(d, i) {}

  render() {}
}

export default Piechart;
`}</code></pre>
    <p>{`We start by importing react and the `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` component, which we’ll use for the arcs. The `}<inlineCode parentName="p">{`constructor`}</inlineCode>{` is going to initialize d3’s pie layout, the `}<inlineCode parentName="p">{`arcGenerator`}</inlineCode>{` is a helper function that makes our code easier to read, and `}<inlineCode parentName="p">{`render`}</inlineCode>{` takes care of rendering.`}</p>
    <p>{`Yes, this is a simple component. As such, it doesn’t `}<em parentName="p">{`really`}</em>{` need to be a class. It could be a functional stateless component, but the code looks messier to me.`}</p>
    <p>{`I’ll show you the functional version later, and you can decide for yourself.`}</p>
    <p>{`Now, the `}<inlineCode parentName="p">{`Piechart`}</inlineCode>{`’s constructor looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
class Piechart extends Component {
    constructor() {
        super();

        this.pie = d3.layout.pie()
                     .value((d) => d.value);
        this.colors = d3.scale.category10();
    }
`}</code></pre>
    <p>{`We use `}<inlineCode parentName="p">{`super()`}</inlineCode>{` to call the parent constructor, then initialize a pie layout with a basic value accessor. That tells it how to get values from our data.`}</p>
    <p>{`Of course, we could avoid this step and re-initialize a new pie layout on every render, but that seems wasteful.`}</p>
    <p>{`The layout is what turns the data into a piechart, by the way. It takes an array of values and returns an array of data in exactly the format that d3’s arc generator expects.`}</p>
    <p>{`It’s great. You’ll see.`}</p>
    <p>{`We also initialize a color scale. We’ll use it to give different colors to piechart slices.`}</p>
    <p>{`The next function we need is `}<inlineCode parentName="p">{`arcGenerator`}</inlineCode>{`. This function returns a `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` component with some props filled in. We `}<em parentName="p">{`could`}</em>{` put this code in the iterator inside `}<inlineCode parentName="p">{`render()`}</inlineCode>{`, but doing it that way gets messy and makes our code harder to read.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
arcGenerator(d, i) {
        return (
            <labeledarc key={\`arc-\${i}\`} data={d} innerradius={this.props.innerRadius} outerradius={this.props.outerRadius} color={this.colors(i)}>
        );
    }
</labeledarc>
`}</code></pre>
    <p>{`We define a `}<inlineCode parentName="p">{`key`}</inlineCode>{` because React yells at us otherwise. We use the current data points `}<inlineCode parentName="p">{`data`}</inlineCode>{`, pass `}<inlineCode parentName="p">{`innerRadius`}</inlineCode>{` and `}<inlineCode parentName="p">{`outerRadius`}</inlineCode>{` from `}<inlineCode parentName="p">{`Piechart`}</inlineCode>{`’s own props, and use the color scale to get a `}<inlineCode parentName="p">{`color`}</inlineCode>{`.`}</p>
    <p>{`Now for the `}<inlineCode parentName="p">{`render`}</inlineCode>{` function:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
    render() {
        let pie = this.pie(this.props.data),
            translate = \`translate(\${this.props.x}, \${this.props.y})\`;

        return (
            <g transform={translate}>
                {pie.map((d, i) => this.arcGenerator(d, i))}
            </g>
        )
    }
`}</code></pre>
    <p>{`We use the data to generate a piechart with `}<inlineCode parentName="p">{`this.pie`}</inlineCode>{`, set up a translation to move our piechart into desired position, then return a grouping element with a bunch of arcs.`}</p>
    <p>{`That’s it. Our piechart shows up on the screen.`}</p>
    <p>{`Or, well, an error shows up because `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` doesn’t exist yet. But once we make that, a piechart is going to show up on screen.`}</p>
    <p>{`Here’s the same code as a functional stateless component. This snippet is 12 lines shorter, but I’m not sure it’s better, per se. It reinitializes both the pie layout and the colors scale on each render, and it awkwardly embeds `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` in the iterator.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const Piechart = ({x, y, innerRadius, outerRadius, data}) => {
    let pie = d3.layout.pie()
                .value((d) => d.value)(data),
        translate = \`translate(\${x}, \${y})\`,
        colors = d3.scale.category10();

    return (
        <g transform={translate}>
            {pie.map((d, i) => (
                <labeledarc key={\`arc-\${i}\`} data={d} innerradius={innerRadius} outerradius={outerRadius} color={colors(i)}>))}
        </labeledarc></g>
    );
};
`}</code></pre>
    <p>{`Use the approach you prefer. In this case, they both work essentially the same way.`}</p>
    <h2 {...{
      "id": "arc"
    }}>{`Arc`}</h2>
    <p>{`Ok, we’ve got the basic `}<inlineCode parentName="p">{`Piechart`}</inlineCode>{`. It tries to draw arcs, but the component for them doesn’t exist yet.`}</p>
    <p>{`When you think about it, a `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` is a type of `}<inlineCode parentName="p">{`Arc`}</inlineCode>{`. It’s a component that draws a pie slice `}<em parentName="p">{`and`}</em>{` a label. So it makes sense to build an `}<inlineCode parentName="p">{`Arc`}</inlineCode>{` component first, then subclass it with `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` and add some features.`}</p>
    <p>{`With ES6 classes, we can do that without funky JavaScript magic. Sure, Babel compiles it to weird funky magic, but we don’t have to worry about it.`}</p>
    <p>{`The base `}<inlineCode parentName="p">{`Arc`}</inlineCode>{` class looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
import React, { Component } from "react";
import d3 from "d3";

class Arc extends Component {
  constructor() {
    super();
  }

  componentWillMount() {
    this.updateD3(this.props);
  }

  componentWillReceiveProps(newProps) {
    this.updateD3(newProps);
  }

  updateD3(newProps) {}

  render() {}
}

export default Arc;
`}</code></pre>
    <p>{`We start by importing React and d3. Then, we define an `}<inlineCode parentName="p">{`Arc`}</inlineCode>{` class which extends `}<inlineCode parentName="p">{`Component`}</inlineCode>{`. In this case, we can’t use functional stateless components for two reasons:`}</p>
    <ol>
      <li parentName="ol">{`We need component lifecycle functions to update the internal state of our arc generator. I’ll show you that in a bit.`}</li>
      <li parentName="ol">{`You can’t extend a function. We `}<em parentName="li">{`could`}</em>{` play with functional composition, but in my experience, that sort of code, in JavaScript at least, is hard to read 2 months later. And it causes interesting issues when debugging.`}</li>
    </ol>
    <p>{`The `}<inlineCode parentName="p">{`Arc`}</inlineCode>{` component has four methods: the constructor, the lifecycle hooks to call `}<inlineCode parentName="p">{`updateD3`}</inlineCode>{`, the method that updates d3 internals’ state, and the render method.`}</p>
    <p>{`All the constructor has to do is instantiate a new `}<inlineCode parentName="p">{`d3.svg.arc`}</inlineCode>{` generator. That looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
class Arc extends Component {
    constructor() {
        super();

        this.arc = d3.svg.arc();
    }
`}</code></pre>
    <p>{`We now have an instance of the arc generator, and we’re going to update its internals in `}<inlineCode parentName="p">{`updateD3`}</inlineCode>{`. If we wanted to set up some arc defaults or constants, this would be a good place.`}</p>
    <p>{`If you aren’t familiar with arc generators, they’re these funny d3.js things that create SVG path definitions. For instance, the “`}<em parentName="p">{`Empty lines`}</em>{`” path definition in that screenshot you saw earlier looks like this:`}</p>
    <p><inlineCode parentName="p">{`M-66.73188112222397,74.47721827437816A100,100 0 0,1 -1.8369701987210297e-14,-100L-9.184850993605149e-15,-50A50,50 0 0,0 -33.365940561111984,37.23860913718908Z`}</inlineCode>{`.`}</p>
    <p>{`You can define any shape in the world with an SVG path definition like that. But it’s not something you ever want to write by hand. Ever. Or even think about, really.`}</p>
    <p>{`So, when you want an arc, use `}<inlineCode parentName="p">{`d3.svg.arc()`}</inlineCode>{` :)`}</p>
    <p>{`The `}<inlineCode parentName="p">{`updateD3`}</inlineCode>{` method looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
    componentWillMount() {
        this.updateD3(this.props);
    }

    componentWillReceiveProps(newProps) {
        this.updateD3(newProps);
    }

    updateD3(newProps) {
        this.arc.innerRadius(newProps.innerRadius);
        this.arc.outerRadius(newProps.outerRadius);
    }
`}</code></pre>
    <p>{`Can you guess what happens here? We update our arc’s inner and outer radius. This lets the users of our component define how big they want arcs to be.`}</p>
    <p>{`Because d3 objects have internal state, we have to make sure to update them every time our props might have changed – `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{`.`}</p>
    <p>{`And yes, you’re right. We could reinitialize the arc on every render. And I still think that feels wasteful and unnecessary.`}</p>
    <p>{`Now for the `}<inlineCode parentName="p">{`render`}</inlineCode>{` method. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
    render() {
        return (
            <path d={this.arc(this.props.data)} style="{{fill:" this.props.color}}=""></path>
        );
    }
`}</code></pre>
    <p>{`As I hinted earlier, we render an SVG path element. The shape definition – `}<inlineCode parentName="p">{`d`}</inlineCode>{` – comes from `}<inlineCode parentName="p">{`this.arc`}</inlineCode>{` and some data, and the color comes from `}<inlineCode parentName="p">{`this.props.color`}</inlineCode>{`.`}</p>
    <p>{`You can render a piechart now. Wonderful, isn’t it? :)`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/04/Screen-Shot-2016-04-12-at-02.08.26-300x118.png",
        "alt": "Screen Shot 2016-04-12 at 02.08.26"
      }}></img></p>
    <h2 {...{
      "id": "labeledarc"
    }}>{`LabeledArc`}</h2>
    <p>{`Piecharts stand on shaky ground as it is. Almost everybody who’s into dataviz hates them. Almost everybody who’s into business intelligence loves them.`}</p>
    <p>{`Both groups hate `}<em parentName="p">{`unlabeled`}</em>{` piecharts, though. Let’s take care of that.`}</p>
    <p>{`To add labels, we’re going to build a `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` component that subclasses `}<inlineCode parentName="p">{`Arc`}</inlineCode>{`. It’s what classes are for, right?`}</p>
    <p>{`Now, even though this component is a fancy render function, we `}<em parentName="p">{`cannot`}</em>{` make it a functional stateless component. We need a reference to the arc generator when calculating coordinates for the label.`}</p>
    <p>{`Let me show you. `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{` looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// ./src/components/Piechart/index.jsx
class LabeledArc extends Arc {
  render() {
    let [labelX, labelY] = this.arc.centroid(this.props.data),
      labelTranslate = \`translate(\${labelX}, \${labelY})\`;

    return (
      <g>
        {super.render()}
        <text transform={labelTranslate} textanchor="middle">
          {this.props.data.data.label}
        </text>
      </g>
    );
  }
}

export { LabeledArc };
`}</code></pre>
    <p>{`We calculate `}<inlineCode parentName="p">{`labelX`}</inlineCode>{` and `}<inlineCode parentName="p">{`labelY`}</inlineCode>{` with `}<inlineCode parentName="p">{`this.arc.centroid(data)`}</inlineCode>{`. This gives us the center point of an arc. I don’t know how the math works. All I know is that it works and that I’m happy somebody made it for me.`}</p>
    <p>{`Notice how we can use `}<inlineCode parentName="p">{`this.arc`}</inlineCode>{` as if it was defined inside `}<inlineCode parentName="p">{`LabeledArc`}</inlineCode>{`. Subclassing is great like that. Don’t go overboard and become an `}<a parentName="p" {...{
        "href": "https://www.google.com/search?q=architecture+astronaut&oq=architecture+astronaut&aqs=chrome..69i57j0l5.2833j0j1&sourceid=chrome&ie=UTF-8"
      }}>{`architecture astronaut`}</a>{`.`}</p>
    <p>{`Next, we put these coordinates into the usual SVG `}<inlineCode parentName="p">{`translate`}</inlineCode>{` transformation and render a grouping element with an arc and a label. We get the arc itself with `}<inlineCode parentName="p">{`super.render()`}</inlineCode>{`, which calls the parent render function, and the label as a `}<inlineCode parentName="p">{`text`}</inlineCode>{` element.`}</p>
    <p>{`I know using `}<inlineCode parentName="p">{`this.props.data.data.label`}</inlineCode>{` to get the text looks weird, but that’s how it works. `}<inlineCode parentName="p">{`this.props.data`}</inlineCode>{` is a single datapoint in the entire piechart, its `}<inlineCode parentName="p">{`.data`}</inlineCode>{` property is where d3’s pie layout puts original data, and `}<inlineCode parentName="p">{`.label`}</inlineCode>{` is how our base data defined labels. It works.`}</p>
    <p>{`Our piechart’s code profile piechart looks like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/04/Screen-Shot-2016-04-12-at-02.08.11-300x122.png",
        "alt": "Screen Shot 2016-04-12 at 02.08.11"
      }}></img></p>
    <p>{`Wonderful.`}</p>
    <h2 {...{
      "id": "voilà"
    }}>{`Voilà`}</h2>
    <p>{`You now have a generic piechart component that you can copy-pasta around your codebase whenever you want.`}</p>
    <p>{`Need a piechart? Use `}<inlineCode parentName="p">{`<Piechart>`}</inlineCode>{` and give it some props. The component has you covered.`}</p>
    <p>{`To recap, here’s what we learned:`}</p>
    <ul>
      <li parentName="ul">{`Piecharts are okay, but not great, dataviz`}</li>
      <li parentName="ul">{`A Piechart takes `}<strong parentName="li">{`53 lines of real code`}</strong>{` to make`}</li>
      <li parentName="ul">{`41 lines with some boilerplate reduction`}</li>
      <li parentName="ul">{`A Piechart is made up of 3 components: Piechart, Arc, LabeledArc`}</li>
      <li parentName="ul">{`D3 makes the hard maths trivial`}</li>
      <li parentName="ul">{`ES6 classes and subclassing are great`}</li>
    </ul>
    <p>{`You can learn more about using React and d3js together in my new book, React+d3js ES6, `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js/"
      }}>{`here`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      